import classNames from 'classnames'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useQueryParams } from 'use-query-params'
import React, { Fragment, useMemo, useState } from 'react'

import { Icon, Show } from '@app/components/util'
import { ContentLayout } from '@app/layouts'
import { LntButton, LntDropdown } from '@app/components/base'
import {
  ExtendedTable,
  Header,
  Subheader,
  PermissionWrapper,
  SubheaderToolbarDivider,
  TableSettingsToolbar
} from '@app/components'
import { FileUploadModal, ACCEPT_FILE_TYPES } from '@app/modals/FileUploadModal'
import { AddCommentModal } from '@app/modals/AddCommentModal'
import { ConfirmModal } from '@app/modals/ConfirmModal'

import { isString } from '@app/typeGuards'
import {
  getResults,
  importMonitoringTasksMassMatch,
  patchTaskResultsItemPriceChecked,
  patchTaskResultsItem,
  createTaskResultsComment
} from '@app/api/routes/monitoringTasks'
import { objectHasEveryNullableProperties } from '@app/utils/object'
import { exportAllMonitoringResultsFile, exportTemplateMonitoringListsFile } from '@app/utils/export'
import { getExportFilesDropdownMenu, isNull, throttled } from '@app/utils'

import {
  useExternalNavigate,
  usePaginationQueryParams,
  useTableSearch,
  useTableSetting,
  useTableSize,
  useToggle,
  useNotification
} from '@app/hooks'

import { FILE_TYPES } from '@app/api/constants'
import { MONITORINGS_TASKS_KEY } from '@app/constants/queryKeys'
import { PROCESSING_RESULTS_PAGE } from '@app/constants/page'
import { BUTTON_TYPE, SIZE } from '@app/constants'
import { TABLE_SETTING_KEYS } from '@app/constants/tableSettingKeys'
import { ICON_NAME } from '@app/constants/icon'
import { PERMISSION } from '@app/constants/permission'
import { isReportFailedError } from '@app/entities/report'

import { TABLE_THROTTLING_DELAY, TABLE_WHEEL_DELTA } from './constants'
import { getColumns, adaptProcessingResultsTableData, adaptGetResultsParams } from './lib'
import { TopFilters } from './ui'
import { useSortingParameters } from './hooks'
import { topFiltersQuerySchema, columnsSearchSchema, tableSearchQuerySchema, sortingScheme } from './configs'

import styles from './styles.module.scss'

import type { Key, ReactNode } from 'react'
import type { FiltersParams } from '@app/types/filter'
import type { GetResultsParams } from '@app/api/routes/monitoringTasks/types'
import type { FileType } from '@app/types'
import type { ProcessingResultsDataSource } from './types'

const columns = getColumns()

const title = 'Обработка результатов'

export const ProcessingResultsPage: React.FC = () => {
  const queryClient = useQueryClient()
  const notification = useNotification()
  const externalNavigate = useExternalNavigate()

  const [selectedIds, setSelectedIds] = useState<number[]>([])

  const { paginationParams, pagination, setPagination, resetCurrentPage } = usePaginationQueryParams()
  const [queryFilters, setQueryFilters] = useQueryParams(topFiltersQuerySchema)

  const { size, onSizeToggle } = useTableSize(['middle', 'large'])

  const changeFromExcelToggle = useToggle()
  const buttonLoadingExportAllToggle = useToggle()
  const pageHeaderToggle = useToggle()
  const addCommentModalToggle = useToggle()
  const deletePriceConfirmModalToggle = useToggle()
  const changePriceTypeConfirmModalToggle = useToggle()

  const headerClassName = classNames(styles.processingResultsPage__header, {
    [styles.processingResultsPage__header_hidden]: pageHeaderToggle.value
  })

  const { columns: columnsWithSorting, sortingParameters: orderBy } = useSortingParameters({ columns, sortingScheme })

  const tasksResultsParams = useMemo(() => {
    // TODO указать правильный тип
    // @ts-expect-error
    const params: GetResultsParams = { ...paginationParams, ...queryFilters, orderBy }

    if (typeof params.isMatchingStatus === 'string') {
      params.isMatchingStatus = params.isMatchingStatus === 'true'
    }
    return params
  }, [paginationParams, queryFilters, orderBy])

  const { data: processingResultsTableData, isLoading: isTasksResultsDataLoading } = useQuery({
    enabled: !objectHasEveryNullableProperties(queryFilters),
    queryKey: [MONITORINGS_TASKS_KEY.tasksResults, tasksResultsParams],
    queryFn: async () => await getResults(tasksResultsParams),
    select: resultsData => adaptProcessingResultsTableData(resultsData)
  })

  const patchItemPriceCheckedMutation = useMutation({
    mutationFn: patchTaskResultsItemPriceChecked,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [MONITORINGS_TASKS_KEY.tasksResults] })

      notification.success({
        message: 'Успешно сохранено'
      })
    }
  })

  const importMonitoringTasksMassMatchMutation = useMutation(importMonitoringTasksMassMatch)

  const patchTaskResultsItemMutation = useMutation({
    mutationFn: patchTaskResultsItem,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [MONITORINGS_TASKS_KEY.tasksResults] })

      notification.success({
        message: 'Успешно сохранено'
      })
    }
  })

  const createTaskResultsCommentMutation = useMutation({
    mutationFn: createTaskResultsComment,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [MONITORINGS_TASKS_KEY.tasksResults] })

      notification.success({
        message: 'Комментарий успешно добавлен'
      })
    }
  })

  const handlePaginationChange = (current: number, pageSize: number): void => setPagination({ current, pageSize })

  const handleFiltersChange = (filters: FiltersParams): void => {
    setQueryFilters(filters)
    resetCurrentPage()
  }

  const handleSelectedRowChange = (selectedKeys: Key[]): void => {
    setSelectedIds(selectedKeys.map(key => (isString(key) ? parseInt(key) : key)))
  }

  const handleNavigateToInnerPage = (_: unknown, record: ProcessingResultsDataSource): void => {
    externalNavigate(`${PROCESSING_RESULTS_PAGE.processingResults}/${record.key}`, '_blank')
  }

  const handleViewSelected = (): void => {
    if (selectedIds.length !== 1 || !processingResultsTableData?.dataSource) return

    const selectedId = selectedIds[0]
    const record = processingResultsTableData.dataSource.find(item => item.key === selectedId)

    if (record) {
      handleNavigateToInnerPage(null, record)
    }
  }

  const handleOnDownloadTemplateExcel = (): void => {
    // TODO уточнить название файла у Андрея З
    const fileName = 'Шаблон'

    exportTemplateMonitoringListsFile(fileName, { file: FILE_TYPES.xlsx })
  }

  const handleImportFromExcel = async (file: File | null): Promise<void> => {
    if (isNull(file)) return

    const formData = new FormData()

    formData.append('file', file, file.name)
    formData.append('fileFormat', FILE_TYPES.xlsx)

    await importMonitoringTasksMassMatchMutation.mutateAsync(formData)

    await queryClient.invalidateQueries({ queryKey: [MONITORINGS_TASKS_KEY.tasksResults] })

    changeFromExcelToggle.setUntoggled()
  }

  const handleExportAllMonitoringResults = async (fileType: FileType): Promise<void> => {
    buttonLoadingExportAllToggle.setToggled()

    const getResultsParams = adaptGetResultsParams(queryFilters)

    const data = {
      ...getResultsParams,
      file: fileType
    }

    const fileName = 'Результаты мониторинга'

    try {
      await exportAllMonitoringResultsFile(fileName, data, { fileType })
    } catch (error) {
      if (isReportFailedError(error)) {
        notification.error({
          message: 'Ошибка выгрузки отчёта:',
          description: error.message
        })
        return
      }

      throw error
    } finally {
      buttonLoadingExportAllToggle.setUntoggled()
    }
  }

  const handleMarkAsChecked = (): void => {
    if (!selectedIds.length) return

    patchItemPriceCheckedMutation.mutate({ resultIds: selectedIds })
  }

  const handleDeletePrice = (): void => {
    if (!selectedIds.length) return

    // Удаляем обе цены, устанавливая их в null
    selectedIds.forEach(id => {
      patchTaskResultsItemMutation.mutate({
        id,
        data: {
          prices: {
            regularPrice: null,
            promoPrice: null
          }
        }
      })
    })

    deletePriceConfirmModalToggle.setUntoggled()
  }

  const handleChangePriceType = (): void => {
    if (!selectedIds.length || !processingResultsTableData?.dataSource) return

    // Для каждой выбранной записи переключаем тип цены
    selectedIds.forEach(id => {
      const record = processingResultsTableData.dataSource.find(item => item.key === id)
      if (!record) return

      const regularPriceValue = record.regularPriceCompetitor?.price ?? null
      const promoPriceValue = record.promoPriceCompetitor?.price ?? null

      const hasRegularPrice = regularPriceValue !== null
      const hasPromoPrice = promoPriceValue !== null

      // Если есть регулярная цена, перемещаем её в акционную и наоборот
      if (hasRegularPrice && !hasPromoPrice) {
        patchTaskResultsItemMutation.mutate({
          id,
          data: {
            prices: {
              regularPrice: null,
              promoPrice: regularPriceValue
            }
          }
        })
      } else if (hasPromoPrice && !hasRegularPrice) {
        patchTaskResultsItemMutation.mutate({
          id,
          data: {
            prices: {
              regularPrice: promoPriceValue,
              promoPrice: null
            }
          }
        })
      } else if (hasRegularPrice && hasPromoPrice) {
        // Если есть обе цены, меняем их местами
        patchTaskResultsItemMutation.mutate({
          id,
          data: {
            prices: {
              regularPrice: promoPriceValue,
              promoPrice: regularPriceValue
            }
          }
        })
      }
    })

    changePriceTypeConfirmModalToggle.setUntoggled()
  }

  const handleAddComment = (data: { message?: string, sampleIds?: number[] }): void => {
    if (!selectedIds.length) return

    // Добавляем комментарий ко всем выбранным записям
    selectedIds.forEach(id => {
      createTaskResultsCommentMutation.mutate({
        id,
        data: {
          message: data.message?.length ? data.message : undefined,
          sampleIds: data.sampleIds
        }
      })
    })
  }

  const {
    items,
    defaultItems,
    columns: modifiedColumns,
    onModify
  } = useTableSetting(TABLE_SETTING_KEYS.processingResultsPage, columnsWithSorting)

  const {
    columns: columnsWithSearchTool,
    filteredDataSource: filteredProcessingResultsDataSource,
    isSearchToolOpened,
    toggleSearchTool
  } = useTableSearch({
    columns: modifiedColumns,
    dataSource: processingResultsTableData?.dataSource,
    columnsSearchSchema,
    tableSearchQuerySchema
  })

  const exportDropdownItems = getExportFilesDropdownMenu(
    'Результаты мониторинга',
    [FILE_TYPES.xlsx, FILE_TYPES.csv],
    handleExportAllMonitoringResults
  )

  const subheaderToolbar = [
    <PermissionWrapper
      key='download'
      requiredPermissions={ [PERMISSION.changeMonitoringResultsPrice] }
    >
      <LntButton
        type={ BUTTON_TYPE.text }
        size={ SIZE.small }
        onClick={ handleOnDownloadTemplateExcel }
      >
        Скачать шаблон
      </LntButton>
    </PermissionWrapper>,

    <PermissionWrapper
      key='change-from-excel'
      requiredPermissions={ [PERMISSION.changeMonitoringResultsPrice] }
    >
      <LntButton
        type={ BUTTON_TYPE.text }
        size={ SIZE.small }
        onClick={ changeFromExcelToggle.setToggled }
      >
        Изменить из excel
      </LntButton>
    </PermissionWrapper>
  ]

  const toolbar: ReactNode[] = [
    <Show
      key='view_change_show'
      condition={ selectedIds.length === 1 }
    >
      <LntButton
        type={ BUTTON_TYPE.text }
        size={ SIZE.small }
        onClick={ handleViewSelected }
      >
        Посмотреть
      </LntButton>

      <SubheaderToolbarDivider />
    </Show>,

    <Show
      key='view_change_delete'
      condition={ selectedIds.length >= 1 }
    >
      <PermissionWrapper requiredPermissions={ [PERMISSION.changeMonitoringResultsPrice] }>
        <LntButton
          type={ BUTTON_TYPE.text }
          size={ SIZE.small }
          onClick={ deletePriceConfirmModalToggle.setToggled }
          loading={ patchTaskResultsItemMutation.isLoading }
        >
          Удалить цену
        </LntButton>
      </PermissionWrapper>

      <SubheaderToolbarDivider />
    </Show>,

    <Fragment key='view_change_type-price-and-stop'>
      <Show condition={ selectedIds.length >= 1 }>
        <PermissionWrapper requiredPermissions={ [PERMISSION.changeMonitoringResultsPrice] }>
          <LntButton
            type={ BUTTON_TYPE.text }
            size={ SIZE.small }
            onClick={ changePriceTypeConfirmModalToggle.setToggled }
            loading={ patchTaskResultsItemMutation.isLoading }
          >
            Изменить тип цены
          </LntButton>
        </PermissionWrapper>

        <PermissionWrapper requiredPermissions={ [PERMISSION.manageMonitoringResultsYourComments] }>
          <LntButton
            type={ BUTTON_TYPE.text }
            size={ SIZE.small }
            onClick={ addCommentModalToggle.setToggled }
            loading={ createTaskResultsCommentMutation.isLoading }
          >
            Оставить комментарий
          </LntButton>
        </PermissionWrapper>

        <PermissionWrapper requiredPermissions={ [PERMISSION.markCorrectMonitoringResults] }>
          <LntButton
            type={ BUTTON_TYPE.text }
            size={ SIZE.small }
            onClick={ handleMarkAsChecked }
            loading={ patchItemPriceCheckedMutation.isLoading }
            disabled={ !selectedIds.length }
          >
            Отметить верным
          </LntButton>
        </PermissionWrapper>

        <SubheaderToolbarDivider />
      </Show>

      <PermissionWrapper requiredPermissions={ [PERMISSION.exportMonitoringResults] }>
        <LntDropdown
          trigger={ ['click'] }
          items={ exportDropdownItems }
        >
          <LntButton
            type={ BUTTON_TYPE.text }
            size={ SIZE.small }
            loading={ buttonLoadingExportAllToggle.value }
          >
            Экспорт
            <Icon name={ ICON_NAME.arrowDown } />
          </LntButton>
        </LntDropdown>
      </PermissionWrapper>

      <SubheaderToolbarDivider />
    </Fragment>,

    <TableSettingsToolbar
      key='table-settings-toolbar'
      tableSizeButtonProps={ { active: size === SIZE.large, onClick: onSizeToggle } }
      tableSettingButtonProps={ { items, defaultItems, onAccept: onModify } }
      tableSearchButtonProps={ { active: isSearchToolOpened, onClick: toggleSearchTool } }
    />
  ]

  const handleScrollUp = (delta: number): void => {
    if (Math.abs(delta) < TABLE_WHEEL_DELTA) return

    if (!pageHeaderToggle.value) return

    pageHeaderToggle.setUntoggled()
  }

  const handleScrollUDown = (delta: number): void => {
    if (Math.abs(delta) < TABLE_WHEEL_DELTA) return

    if (pageHeaderToggle.value) return

    pageHeaderToggle.setToggled()
  }

  return (
    <>
      { /* TODO: нужно убрать тег header из атомарных компонентов и оставлять его только на уровне страницы/крупной секции */ }
      <header className={ headerClassName }>
        <Header controlProps={ { title } } />

        <Subheader toolbar={ subheaderToolbar } />

        <TopFilters
          onSearch={ handleFiltersChange }
          initialFilters={ queryFilters as FiltersParams }
        />
      </header>

      <ContentLayout>
        <ExtendedTable
          size={ size }
          loading={ isTasksResultsDataLoading }
          columns={ columnsWithSearchTool }
          dataSource={ filteredProcessingResultsDataSource }
          toolbar={ toolbar }
          rowSelection={ {
            onChange: handleSelectedRowChange
          } }
          pagination={ {
            ...pagination,
            total: processingResultsTableData?.count,
            onChange: handlePaginationChange
          } }
          onRowDoubleClick={ handleNavigateToInnerPage }
          onWheelUp={ throttled(handleScrollUp, TABLE_THROTTLING_DELAY) }
          onWheelDown={ throttled(handleScrollUDown, TABLE_THROTTLING_DELAY) }
        />
      </ContentLayout>

      <FileUploadModal
        title='Изменить из excel'
        accept={ ACCEPT_FILE_TYPES.xlsx }
        open={ changeFromExcelToggle.value }
        onCancel={ changeFromExcelToggle.setUntoggled }
        onFileUpload={ handleImportFromExcel }
      />

      <AddCommentModal
        open={ addCommentModalToggle.value }
        onCancel={ addCommentModalToggle.setUntoggled }
        onSubmit={ handleAddComment }
      />

      <ConfirmModal
        open={ deletePriceConfirmModalToggle.value }
        onCancel={ deletePriceConfirmModalToggle.setUntoggled }
        onConfirm={ handleDeletePrice }
        title='Вы действительно хотите удалить цену?'
        okText='Да'
        cancelText='Нет'
      />

      <ConfirmModal
        open={ changePriceTypeConfirmModalToggle.value }
        onCancel={ changePriceTypeConfirmModalToggle.setUntoggled }
        onConfirm={ handleChangePriceType }
        title='Вы действительно хотите изменить тип цены?'
        okText='Да'
        cancelText='Нет'
      />
    </>
  )
}
